/**
 * AgronomyCalendarStrip — 14-day forward-looking agronomy calendar.
 *
 * Reads from GET /api/alerts/calendar?days=14 and renders a horizontally
 * scrollable strip of daily tiles. Each tile surfaces the one big call a
 * golf super would make that day (irrigate, fungicide, disease-watch,
 * rain-skip, or quiet). Tap a tile to expand the full read.
 *
 * Palette is tuned for the "dark kiosk + light dashboard" split so the
 * strip reads well on /irrigation, /dashboard, and the widget in a
 * future pass.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Calendar, Droplets, ShieldAlert, CloudRain, CheckCircle2, FlaskConical, AlertTriangle } from 'lucide-react';
import { API_URL } from '../config/api';

const KIND_STYLE = {
  irrigate_urgent: { icon: AlertTriangle, accent: 'bg-red-500', ring: 'ring-red-500/50', text: 'text-red-700 dark:text-red-300' },
  irrigate:        { icon: Droplets,      accent: 'bg-amber-500', ring: 'ring-amber-500/40', text: 'text-amber-700 dark:text-amber-300' },
  fungicide:       { icon: FlaskConical,  accent: 'bg-fuchsia-500', ring: 'ring-fuchsia-500/40', text: 'text-fuchsia-700 dark:text-fuchsia-300' },
  disease_watch:   { icon: ShieldAlert,   accent: 'bg-sky-500', ring: 'ring-sky-500/40', text: 'text-sky-700 dark:text-sky-300' },
  rain_skip:       { icon: CloudRain,     accent: 'bg-blue-500', ring: 'ring-blue-500/40', text: 'text-blue-700 dark:text-blue-300' },
  quiet:           { icon: CheckCircle2,  accent: 'bg-emerald-500', ring: 'ring-emerald-500/30', text: 'text-emerald-700 dark:text-emerald-300' },
};

const madTone = (label) => {
  if (label === 'STRESS' || label === 'WILT') return 'text-red-700 dark:text-red-400';
  if (label === 'BREACH') return 'text-amber-700 dark:text-amber-400';
  return 'text-emerald-700 dark:text-emerald-400';
};

export const AgronomyCalendarStrip = () => {
  const [state, setState] = useState({ loading: true, rows: [], err: null, mad_threshold_pct: 60 });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/alerts/calendar?days=14`, { withCredentials: true });
        if (!alive) return;
        if (r.data?.error) {
          setState({ loading: false, rows: [], err: r.data.error, mad_threshold_pct: 60 });
        } else {
          setState({
            loading: false,
            rows: r.data?.rows || [],
            err: null,
            mad_threshold_pct: r.data?.mad_threshold_pct || 60,
          });
        }
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, rows: [], err: 'fetch_failed', mad_threshold_pct: 60 });
      }
    })();
    return () => { alive = false; };
  }, []);

  if (state.loading) {
    return (
      <Card data-testid="agronomy-calendar-loading">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4" /> 14-Day Agronomy Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 overflow-x-auto">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="w-24 h-28 rounded-lg flex-shrink-0" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (state.err === 'no_location') {
    return (
      <Card data-testid="agronomy-calendar-no-location">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4" /> 14-Day Agronomy Calendar
          </CardTitle>
          <CardDescription>Set your property location on Profile → Location to unlock the 14-day forward read.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (state.err || !state.rows.length) {
    return null; // fail-soft — don't spam an error card on /irrigation
  }

  const sel = state.rows[selected] || state.rows[0];
  const topCall = sel.calls?.[0];
  const topStyle = topCall ? (KIND_STYLE[topCall.kind] || KIND_STYLE.quiet) : KIND_STYLE.quiet;

  return (
    <Card data-testid="agronomy-calendar-strip">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-600" /> 14-Day Agronomy Calendar
              <Badge variant="outline" className="ml-2 border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/5">Super's view</Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              MAD threshold <b>{state.mad_threshold_pct}%</b> · one call per day, the way a golf super reads the week ahead.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Horizontal strip */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
          {state.rows.map((row, i) => {
            const primary = row.calls?.[0] || { kind: 'quiet', label: '✓ Steady' };
            const style = KIND_STYLE[primary.kind] || KIND_STYLE.quiet;
            const Icon = style.icon;
            const isSel = i === selected;
            return (
              <button
                key={row.date_iso}
                data-testid={`agronomy-day-tile-${i}`}
                onClick={() => setSelected(i)}
                className={`flex-shrink-0 w-24 snap-start rounded-lg border p-2 text-left transition
                  ${isSel ? `ring-2 ${style.ring} border-transparent shadow-sm` : 'border-border/60 hover:border-border'}
                  bg-card`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] tracking-widest text-muted-foreground uppercase font-semibold">{row.dow}</span>
                  <span className={`w-2 h-2 rounded-full ${style.accent}`} />
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">{row.date_iso.slice(5)}</div>
                <div className="mt-1 flex items-center gap-1.5">
                  <Icon className={`w-3.5 h-3.5 ${style.text}`} />
                  <span className={`text-[11px] font-semibold ${madTone(row.mad_label)}`}>{row.mad_pct}%</span>
                </div>
                <div className="mt-0.5 text-[10px] text-muted-foreground truncate">{row.mad_label}</div>
                {row.rain_in > 0 && (
                  <div className="mt-0.5 text-[10px] text-blue-600 dark:text-blue-400">☔ {row.rain_in}"</div>
                )}
              </button>
            );
          })}
        </div>

        {/* Detail panel for selected day */}
        <div className="mt-3 rounded-lg border border-border/60 p-3 bg-muted/30" data-testid="agronomy-detail-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-6 rounded-sm ${topStyle.accent}`} />
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest font-semibold">
                  {sel.dow} · {sel.date_iso}
                </div>
                <div className={`text-sm font-semibold ${topStyle.text}`}>
                  {topCall ? topCall.label : 'Steady'}
                </div>
              </div>
            </div>
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span><b className="text-foreground">{sel.tmax_f}°/{sel.tmin_f}°</b> F</span>
              <span>ET <b className="text-foreground">{sel.et_in}"</b></span>
              <span>Dew <b className="text-foreground">{sel.dewpoint_f}°F</b></span>
              <span>Smith-Kerns <b className={sel.dollar_spot_pct >= 50 ? 'text-fuchsia-600 dark:text-fuchsia-400' : 'text-foreground'}>{sel.dollar_spot_pct}%</b></span>
            </div>
          </div>
          {sel.calls && sel.calls.length > 1 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {sel.calls.slice(1).map((c, j) => (
                <Badge key={j} variant="outline" className="text-[10px] font-normal">
                  {c.label}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <p className="mt-2 text-[11px] text-muted-foreground">
          The Super's Brief email (4:30 AM local) and alert engine pull from these same thresholds.
        </p>
      </CardContent>
    </Card>
  );
};

export default AgronomyCalendarStrip;
